import { useNavigate } from "react-router-dom";
import { TopBar } from "@/components/TopBar";
import { motion } from "framer-motion";
import { ArrowLeft, Printer, Info, ListChecks, Ear, PenLine, ClipboardCheck, Cross, Clock, CheckCircle2, Lightbulb } from "lucide-react";

const DAYS = [
  {
    n: 1, title: "How It Works", icon: Info, color: "#E07A5F",
    goal: "Introduce the routine so the student knows what to expect all week.",
    teacher: [
      "Explain in your own warm words: Spelling Power is a short, ~15-minute-a-day habit where we only study the words a student actually misses — not one big list everyone memorizes.",
      "Make it clear this whole week is placement: we're simply finding the right starting point. None of it is a grade.",
      "Reassure the student that missing words on the tests this week is expected and helpful — it's how we find their level.",
    ],
    student: "The student opens \"Spelling Power — Day 1: How It Works\" and, after your chat, writes one or two sentences explaining how Spelling Power works and why we only study the words we miss.",
    tips: "Keep it light and encouraging. If the child is anxious about tests, this is the day to take the pressure off.",
    verse: "The heart of the discerning acquires knowledge, for the ears of the wise seek it out.",
    reference: "Proverbs 18:15 (NIV)",
  },
  {
    n: 2, title: "Survey Test (Form A)", icon: Ear, color: "#81B29A",
    goal: "Get a first, quick read on the student's level with a short dictation test.",
    teacher: [
      "Sit with the student and dictate words using the three-step rhythm: say the word, use it in a simple sentence, then say the word again.",
      "Give no hints and don't let the student self-correct — you want their natural, first-try spelling.",
      "Stop after a few misses in a row; a short test is all you need here. Keep track of which words were missed.",
    ],
    student: "The student writes each dictated word on paper, then opens \"Day 2: Survey Test (Form A)\" and reflects: how did it go, and one word they'd like to spell better.",
    tips: "Speak clearly and at a steady pace. This is diagnostic — resist the urge to coach mid-test.",
    verse: "Turning your ear to wisdom and applying your heart to understanding.",
    reference: "Proverbs 2:2 (NIV)",
  },
  {
    n: 3, title: "Placement Test", icon: ClipboardCheck, color: "#F4A261",
    goal: "Pinpoint the exact starting level using a fuller test chosen from yesterday's result.",
    teacher: [
      "Based on the Survey Test, choose the placement test that fits and give the whole test this time.",
      "Use the same rhythm: word, sentence, word. Again — no hints and no self-correcting.",
      "Count the misses to set the student's starting level for regular daily spelling (which begins next week).",
    ],
    student: "The student takes the full test on paper, then opens \"Day 3: Placement Test\" and writes one sentence about a spelling rule or pattern that felt tricky (silent letters, double letters, vowel patterns, etc.).",
    tips: "Jot a note of the patterns the student struggles with — it'll help you plan their word lists next week.",
    verse: "Do your best to present yourself to God as one approved, a worker who does not need to be ashamed and who correctly handles the word of truth.",
    reference: "2 Timothy 2:15 (NIV)",
  },
  {
    n: 4, title: "Writing Sample", icon: PenLine, color: "#3D405B",
    goal: "See how the student spells naturally in real writing, to fine-tune placement.",
    teacher: [
      "Have the student write a short piece entirely on their own — no spell-check and no dictionary.",
      "Encourage natural, honest writing; the goal is to see everyday spelling, not perfection.",
      "Compare the sample against the tests to confirm or gently adjust the starting level.",
    ],
    student: "The student opens \"Day 4: Writing Sample\" and writes a short paragraph (4–6 sentences) about their day or a favorite topic — no spell-check or dictionary.",
    tips: "If they freeze up, offer a friendly prompt (a favorite meal, a pet, this weekend). Then you're set for regular daily spelling next week.",
    verse: "Whatever you do, work at it with all your heart, as working for the Lord, not for human masters.",
    reference: "Colossians 3:23 (NIV)",
  },
];

export default function TeacherGuideSpelling() {
  const navigate = useNavigate();
  return (
    <div>
      <div className="no-print"><TopBar subtitle="Teacher's Guide" /></div>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff; } .guide-card { break-inside: avoid; } }`}</style>

      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <div className="no-print flex items-center justify-between mb-6">
          <button onClick={() => navigate("/parent")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={16} /> Dashboard</button>
          <button data-testid="print-guide-btn" onClick={() => window.print()} className="flex items-center gap-2 rounded-full bg-[#3D405B] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#2c2f45] transition-colors"><Printer size={16} /> Print guide</button>
        </div>

        <header className="mb-8">
          <p className="stat text-xs uppercase tracking-widest text-[#E07A5F] font-semibold">Teacher's Guide</p>
          <h1 className="font-head text-4xl font-bold text-[#3D405B] mt-1">Spelling Power — Week 1 (Placement)</h1>
          <p className="text-[#6B7280] mt-2 leading-relaxed">A day-by-day guide for the first week. This week is <b>placement</b> — its only job is to find each child's right starting level. Nothing this week is a grade. Regular daily spelling begins next week.</p>
        </header>

        {/* At a glance */}
        <div className="guide-card rounded-2xl border border-[#EAE6DF] bg-white p-5 mb-6">
          <h2 className="font-head font-semibold text-[#3D405B] mb-3 flex items-center gap-2"><ListChecks size={18} className="text-[#81B29A]" /> At a glance</h2>
          <ul className="space-y-2 text-sm text-[#3D405B]">
            <li className="flex gap-2"><Clock size={16} className="text-[#6B7280] shrink-0 mt-0.5" /> About <b>15 minutes a day</b>, Monday–Thursday.</li>
            <li className="flex gap-2"><CheckCircle2 size={16} className="text-[#81B29A] shrink-0 mt-0.5" /> Students only study the words they actually <b>miss</b> — not a memorized list.</li>
            <li className="flex gap-2"><Info size={16} className="text-[#F4A261] shrink-0 mt-0.5" /> All four days are <b>placement</b>, not graded. Levels are based on word patterns, not school grade.</li>
          </ul>
        </div>

        {/* How to dictate */}
        <div className="guide-card rounded-2xl border border-[#A8DADC]/50 bg-[#A8DADC]/10 p-5 mb-8">
          <h2 className="font-head font-semibold text-[#3D405B] mb-2 flex items-center gap-2"><Ear size={18} className="text-[#457B9D]" /> How to give a dictation test</h2>
          <p className="text-sm text-[#3D405B] leading-relaxed">Use the same simple rhythm every time: <b>say the word → use it in a short sentence → say the word again</b>. Then let the student write it. Give no hints, and don't let them self-correct — you want their natural spelling. Speak clearly at a steady pace.</p>
        </div>

        {/* Day by day */}
        <h2 className="font-head text-2xl font-bold text-[#3D405B] mb-4">Day by day</h2>
        <div className="space-y-4 mb-8">
          {DAYS.map((d, i) => {
            const Icon = d.icon;
            return (
              <motion.div key={d.n} data-testid={`guide-day-${d.n}`}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.3) }}
                className="guide-card rounded-2xl border border-[#EAE6DF] bg-white p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="grid place-items-center w-10 h-10 rounded-xl text-white shrink-0" style={{ background: d.color }}><Icon size={19} /></span>
                  <div>
                    <p className="stat text-xs text-[#6B7280]">Day {d.n}</p>
                    <h3 className="font-head text-xl font-bold text-[#3D405B]">{d.title}</h3>
                  </div>
                </div>
                <p className="text-sm text-[#3D405B] mb-3"><b>Goal:</b> {d.goal}</p>

                <p className="text-xs font-semibold uppercase tracking-wide text-[#6B7280] mb-1">What the teacher does</p>
                <ul className="space-y-1.5 mb-3">
                  {d.teacher.map((t, j) => (
                    <li key={j} className="flex gap-2 text-sm text-[#3D405B]"><span className="mt-1.5 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: d.color }} /> {t}</li>
                  ))}
                </ul>

                <p className="text-xs font-semibold uppercase tracking-wide text-[#6B7280] mb-1">What the student does in the app</p>
                <p className="text-sm text-[#3D405B] mb-3">{d.student}</p>

                <div className="rounded-xl bg-[#F4A261]/10 border border-[#F4A261]/30 p-3 mb-3 flex gap-2">
                  <Lightbulb size={15} className="text-[#F4A261] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#3D405B]"><b>Tip:</b> {d.tips}</p>
                </div>

                <div className="rounded-xl bg-[#3D405B]/5 border border-[#3D405B]/10 p-3 flex gap-2">
                  <Cross size={15} className="text-[#3D405B] shrink-0 mt-0.5" />
                  <p className="text-sm italic text-[#3D405B]">"{d.verse}" <span className="not-italic text-[#6B7280]">— {d.reference}</span></p>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="guide-card rounded-2xl border border-[#81B29A]/40 bg-[#81B29A]/10 p-5">
          <h2 className="font-head font-semibold text-[#3D405B] mb-2 flex items-center gap-2"><CheckCircle2 size={18} className="text-[#81B29A]" /> After the week</h2>
          <p className="text-sm text-[#3D405B] leading-relaxed">Use the tests and writing sample to set each child's starting level, and note the patterns they found tricky. Next week, add their daily words in the app under <b>Spelling Word Bank</b>, and the built-in <b>Spelling Bee</b> mini-game will practice exactly those words.</p>
        </div>
      </main>
    </div>
  );
}
